import React, {useEffect, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {ArrowLeft, Loader2} from "lucide-react";
import CourseNavbar from "../components/landing/CourseNavbar";
import {SearchProvider} from "../components/home-components/SearchContext";
import Footer from "../components/Footer";
import {useCart} from "../components/CardContext";
import {useUserPublicCatalog} from "../store/user";
import {imageUrl, money, shortDate} from "../lib/format";
import {stripHtml} from "../components/landing/publicCourseMap";
import {materialTypeLabel} from "../components/landing/publicMaterialMap";
import authSession from "../lib/authSession";
import {useUserAuth} from "../store/user";

function DetailSkeleton() {
  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <div className="h-4 w-28 animate-pulse bg-slate-200" />
      <div className="mt-5 h-9 w-2/3 animate-pulse bg-slate-200" />
      <div className="mt-3 h-4 w-1/2 animate-pulse bg-slate-200" />
      <div className="mt-8 grid gap-8 lg:grid-cols-[280px_1fr]">
        <div className="h-72 animate-pulse bg-slate-200" />
        <div className="space-y-3">
          <div className="h-4 animate-pulse bg-slate-200" />
          <div className="h-4 animate-pulse bg-slate-200" />
          <div className="h-4 w-3/4 animate-pulse bg-slate-200" />
        </div>
      </div>
    </div>
  );
}

function MaterialDetailContent() {
  const {type, idOrSlug} = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {userIsAuthenticated} = useUserAuth();
  const authSnapshot = React.useSyncExternalStore(authSession.subscribe, authSession.getSnapshot);
  const {addToCart, openCart} = useCart();
  const {fetchPublicResourceDetail, fetchPublicResourcePreview} = useUserPublicCatalog();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState(null);

  const load = () => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPreview(null);
    setPreviewError(null);
    setPreviewLoading(true);

    fetchPublicResourceDetail(type, idOrSlug).then((result) => {
      if (cancelled) return;
      setLoading(false);
      if (result.success) setDetail(result.data);
      else setError(result.error);
    });

    fetchPublicResourcePreview(type, idOrSlug).then((result) => {
      if (cancelled) return;
      setPreviewLoading(false);
      if (result.success) setPreview(result.data);
      else setPreviewError(result.error);
    });

    return () => {
      cancelled = true;
    };
  };

  useEffect(() => {
    const cancel = load();
    return cancel;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, idOrSlug]);

  const authenticated = userIsAuthenticated || Boolean(detail?.viewer?.isAuthenticated) || (authSnapshot.status === "authenticated" && authSnapshot.role === "user");
  useEffect(() => {
    if (!authenticated || !detail || !new URLSearchParams(location.search).has("study")) return;
    document.getElementById("material-preview")?.scrollIntoView({behavior: "smooth", block: "start"});
    navigate(location.pathname, {replace: true});
  }, [authenticated, detail, location.pathname, location.search, navigate]);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <CourseNavbar />
        <DetailSkeleton />
        <Footer />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="min-h-screen bg-white">
        <CourseNavbar />
        <main className="mx-auto max-w-2xl px-4 py-24 text-center">
          <p className="text-sm text-slate-500">{error || "This material could not be found."}</p>
          <div className="mt-5 flex justify-center gap-2">
            <button
              type="button"
              onClick={load}
              className="bg-blue-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={goBack}
              className="border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Go back
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const plain = (value) => stripHtml(value || "");
  const price = Number(detail.price) || 0;
  const isFree = Boolean(detail.isFree || price === 0);
  const cover = imageUrl(detail.thumbnailUrl);
  const person = detail.resourcePerson;
  const departments = Array.isArray(detail.departments) ? detail.departments : [];
  const included = Array.isArray(detail.whatIsIncluded) ? detail.whatIsIncluded : [];
  const contents = Array.isArray(detail.tableOfContents) ? detail.tableOfContents : [];
  const purchased = Boolean(detail.viewer?.isPurchased);
  const typeLabel =
    materialTypeLabel(detail.type || type) || detail.itemType || "Material";
  const previewText = plain(preview?.chapterOnePreview ?? detail.chapterOnePreview);
  const cartType =
    detail.type === "books"
      ? "book"
      : detail.type === "journals"
        ? "journal"
        : detail.type || detail.itemType || "material";

  const addMaterialToCart = () => {
    addToCart({
      id: detail.id,
      type: cartType,
      title: detail.title,
      instructor: person?.name || typeLabel,
      thumbnail: cover || "/images/course_placeholder.jpg",
      category: typeLabel,
      level: detail.academicLevel || "",
      price,
    });
    openCart();
  };
  const buyMaterialNow = () => {
    addToCart({
      id: detail.id,
      type: cartType,
      title: detail.title,
      instructor: person?.name || typeLabel,
      thumbnail: cover || "/images/course_placeholder.jpg",
      category: typeLabel,
      level: detail.academicLevel || "",
      price,
    });
    navigate(`/cart?checkoutId=${encodeURIComponent(detail.id)}&checkoutType=${encodeURIComponent(cartType)}`);
  };
  const studyPath = `${location.pathname}?study=1#material-preview`;
  const handleStudyMaterial = () => {
    if (!authenticated) {
      navigate(`/login?next=${encodeURIComponent(studyPath)}`);
      return;
    }
    document.getElementById("material-preview")?.scrollIntoView({behavior: "smooth", block: "start"});
  };

  return (
    <div className="min-h-screen bg-white">
      <CourseNavbar />
      <main className="mx-auto mb-12 max-w-6xl px-4 py-10">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition-colors hover:text-blue-800"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="mt-5 grid gap-8 lg:grid-cols-[280px_1fr]">
          <div>
            <div className="overflow-hidden border border-slate-200 bg-slate-100">
              {cover ? (
                <img
                  src={cover}
                  alt={detail.title}
                  className="h-72 w-full object-cover"
                  loading="eager"
                />
              ) : (
                <div className="flex h-72 items-center justify-center text-sm text-slate-400">
                  No cover image
                </div>
              )}
            </div>

            <div className="mt-4 border border-slate-200 bg-slate-50 p-4">
              <p className="m-0 text-xs uppercase tracking-wide text-slate-400">Price</p>
              <p className="m-0 mt-1 text-2xl font-bold text-slate-900">
                {isFree ? "Free" : money(price)}
              </p>

              {purchased ? (
                <p className="m-0 mt-3 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
                  You own this material
                </p>
              ) : isFree ? (
                <div className="mt-3">
                  <p className="m-0 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-800">Free resource</p>
                  <button type="button" onClick={handleStudyMaterial} className="mt-2 w-full bg-blue-900 px-3 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-800">
                    {authenticated ? "Continue studying" : "Sign in to study"}
                  </button>
                </div>
              ) : (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={buyMaterialNow}
                    className="bg-blue-900 px-3 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-800"
                  >
                    Buy now
                  </button>
                  <button
                    type="button"
                    onClick={addMaterialToCart}
                    className="border border-blue-900 px-3 py-2.5 text-sm font-semibold text-blue-900 transition-colors hover:bg-blue-50"
                  >
                    Add to cart
                  </button>
                </div>
              )}

              {!authenticated && !isFree ? (
                <p className="m-0 mt-2 text-xs text-slate-500">
                  Sign in to complete checkout.
                </p>
              ) : null}
            </div>
          </div>

          <div>
            <span className="inline-flex items-center bg-blue-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-blue-700">
              {typeLabel}
            </span>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              {detail.title}
            </h1>
            {detail.summary ? (
              <p className="mt-3 text-base leading-relaxed text-slate-600">{detail.summary}</p>
            ) : null}

            <dl className="mt-6 grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
              {[
                ["Item type", detail.itemType],
                ["Resource person", person?.name],
                ["Delivery", detail.deliveryMethod],
                ["Level", detail.academicLevel],
                ["Field", detail.fieldOfStudy],
                ["Format", detail.fileFormat],
                ["Published", shortDate(detail.publishedAt)],
              ].map(([label, value]) =>
                value ? (
                  <div key={label}>
                    <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
                    <dd className="m-0 text-slate-800">{value}</dd>
                  </div>
                ) : null
              )}
            </dl>

            {person?.bio ? (
              <p className="mt-4 text-sm leading-relaxed text-slate-600">{plain(person.bio)}</p>
            ) : null}

            {departments.length ? (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {departments.map((department) => (
                  <span
                    key={department.slug || department.name}
                    className="bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-800"
                  >
                    {department.name}
                  </span>
                ))}
              </div>
            ) : null}

            {[
              ["Description", plain(detail.description)],
              ["Abstract", plain(detail.abstract)],
            ].map(([label, value]) =>
              value ? (
                <section key={label} className="mt-6">
                  <h2 className="m-0 text-base font-semibold text-slate-900">{label}</h2>
                  <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-slate-600">
                    {value}
                  </p>
                </section>
              ) : null
            )}

            <section id="material-preview" className="mt-6 scroll-mt-24">
              <h2 className="m-0 text-base font-semibold text-slate-900">
                Chapter one preview
              </h2>
              {isFree && !authenticated ? (
                <div className="mt-3 border border-blue-100 bg-blue-50 p-4">
                  <p className="m-0 text-sm font-semibold text-slate-800">Sign in to continue studying this free resource.</p>
                  <button type="button" onClick={handleStudyMaterial} className="mt-3 bg-blue-900 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800">Sign in to continue</button>
                </div>
              ) : (
                <>
                  {preview?.title ? <p className="m-0 mt-1 text-xs text-slate-500">{preview.title}</p> : null}
                  {previewLoading ? (
                    <p className="mt-2 flex items-center gap-2 text-sm text-slate-500">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
                    </p>
                  ) : previewError ? (
                    <p className="mt-2 bg-amber-50 px-3 py-2 text-sm text-amber-700">{previewError}</p>
                  ) : previewText ? (
                    <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-600">{previewText}</p>
                  ) : (
                    <p className="mt-2 text-sm text-slate-500">No chapter-one preview is available for this material.</p>
                  )}
                </>
              )}
            </section>

            {included.length ? (
              <section className="mt-6">
                <h2 className="m-0 text-base font-semibold text-slate-900">What is included</h2>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600">
                  {included.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>
            ) : null}

            {contents.length ? (
              <section className="mt-6">
                <h2 className="m-0 text-base font-semibold text-slate-900">Table of contents</h2>
                <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-slate-600">
                  {contents.map((item, index) => (
                    <li key={index}>{typeof item === "string" ? item : JSON.stringify(item)}</li>
                  ))}
                </ol>
              </section>
            ) : null}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

const MaterialDetail = () => (
  <SearchProvider>
    <MaterialDetailContent />
  </SearchProvider>
);

export default MaterialDetail;
